import React, { Component } from 'react';
import { withTranslation } from '../../lib/translate.js';
import { SmallText } from '../common/index.js';
import { forwardTo } from '../../lib/utils.js';
import { getNavigationItems } from './items.js';
import './index.css';

const SCALE = { small: 'scale(1.1, 1)', medium: 'scale(1.2)', large: 'scale(1.3)', xlarge: 'scale(1.15, 1.4)' };
const JELLY = {
  moveRight: [
    { offset: 0, transform: SCALE.large },
    { offset: 0.4, transform: SCALE.small },
    { offset: 0.75, transform: SCALE.xlarge },
    { offset: 1, transform: SCALE.large },
  ],
  moveLeft: [
    { offset: 0, transform: SCALE.large },
    { offset: 0.1, transform: SCALE.xlarge },
    { offset: 0.6, transform: SCALE.small },
    { offset: 1, transform: SCALE.large },
  ],
  slowly: [
    { offset: 0, transform: SCALE.large },
    { offset: 0.4, transform: SCALE.medium },
    { offset: 1, transform: SCALE.large },
  ],
};

class BottomNavigationIOS extends Component {
  constructor(props) {
    super(props);
    this.wrapperRef = React.createRef();
    this.pillRef = React.createRef();
    this.startX = 0;
    this.dragged = false;
    this.hovered = -1;
    this.slide = null;
    this.jelly = null;
    this.samples = [];
    this.maxVelocity = 0;
    this.bounds = [];
    this.left = 0;
    this.width = 0;
    this.minX = 0;
    this.maxX = 0;
    this.leaveTimer = null;
    this.state = { dragging: false, hovered: -1, selected: -1 };
  }

  componentWillUnmount() {
    clearTimeout(this.leaveTimer);
    this.pillRef.current?.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
  }

  itemNodes = () => [...this.wrapperRef.current.querySelectorAll(':scope > div')];

  itemIndexAt = (clientX) => this.bounds.findIndex(([left, right]) => clientX >= left && clientX <= right);

  pillX = (clientX) => Math.min(Math.max(clientX - this.left - this.width / 2, this.minX), this.maxX);

  moveTo = (x) => {
    this.pillRef.current.style.transform = `translate3d(${x}px, 0, 0)`;
  };

  animate = (keyframes, duration) => this.pillRef.current.firstChild.animate(keyframes, { duration, easing: 'ease-out' });

  setHovered = (index) => {
    this.hovered = index;
    this.setState({ hovered: index });
  };

  reset = (selected = -1) => {
    this.pillRef.current.style.display = 'none';
    this.slide?.cancel();
    this.pillRef.current.firstChild.getAnimations().forEach((animation) => animation.cancel());
    this.slide = this.jelly = null;
    this.hovered = -1;
    this.setState({ dragging: false, hovered: -1, selected });
  };

  squash = (onFinish) => {
    const inner = this.pillRef.current.firstChild;
    const from = getComputedStyle(inner).transform;
    inner.getAnimations().forEach((animation) => animation.cancel());
    this.jelly = null;
    inner.animate([{ transform: from }, { transform: 'scale(1, 0.92)' }], { duration: 120, easing: 'ease-out', fill: 'forwards' }).onfinish = onFinish;
  };

  dragStart = (clientX) => {
    if (this.hovered >= 0) return;
    const items = this.itemNodes();
    this.bounds = items.map((item) => {
      const rect = item.getBoundingClientRect();
      return [rect.left, rect.right];
    });
    const index = this.itemIndexAt(clientX);
    if (index < 0) return;
    const origin = items.find((item) => item.classList.contains('navigation-active')) || items[index];
    this.dragged = false;
    this.startX = clientX;
    this.samples = [{ x: clientX, t: performance.now() }];
    this.maxVelocity = 0;
    this.width = origin.offsetWidth;
    this.minX = items[0].offsetLeft;
    this.maxX = items[items.length - 1].offsetLeft;
    const wrapper = this.wrapperRef.current;
    const pill = this.pillRef.current;
    this.left = wrapper.getBoundingClientRect().left;
    const height = (wrapper.offsetHeight + 10) / 1.3;
    pill.style.display = '';
    pill.style.width = `${this.width}px`;
    pill.style.height = `${height}px`;
    pill.style.top = `${(wrapper.offsetHeight - height) / 2}px`;
    this.moveTo(origin.offsetLeft);
    this.hovered = index;
    this.setState({ dragging: true, hovered: index });
    this.animate([{ transform: 'scale(1)' }, { transform: SCALE.large }], 200);
    const target = items[index].offsetLeft;
    if (target === origin.offsetLeft) return;
    this.slide = pill.animate(
      [{ transform: `translate3d(${origin.offsetLeft}px, 0, 0)` }, { transform: `translate3d(${target}px, 0, 0)` }],
      { duration: Math.max(Math.abs(target - origin.offsetLeft), 140) * 2.1, easing: 'ease-out', fill: 'forwards' },
    );
    this.slide.onfinish = () => this.slideEnd(target);
  };

  slideEnd = (x) => {
    this.moveTo(x);
    this.slide.cancel();
    this.slide = null;
  };

  dragMove = (clientX) => {
    if (this.hovered < 0 || this.slide) return;
    if (!this.dragged && Math.abs(clientX - this.startX) < 6) return;
    this.dragged = true;
    const now = performance.now();
    this.samples = [...this.samples.filter((s) => now - s.t < 100), { x: clientX, t: now }];
    const first = this.samples[0];
    const velocity = now > first.t ? (clientX - first.x) / (now - first.t) : 0;
    const speed = Math.abs(velocity);
    if (!this.jelly) {
      this.maxVelocity = Math.max(this.maxVelocity, speed);
      if (speed > 0.2) this.jelly = this.animate(JELLY.slowly, 720);
      if (this.maxVelocity > 0.2 && speed < 0.15 && Math.abs(clientX - this.startX) > 100) {
        this.jelly = this.animate(JELLY[velocity > 0 ? 'moveRight' : 'moveLeft'], 720);
        this.maxVelocity = 0;
      }
      if (this.jelly) this.jelly.onfinish = () => (this.jelly = null);
    }
    const index = this.itemIndexAt(clientX);
    if (index >= 0 && index !== this.hovered) this.setHovered(index);
    this.moveTo(this.pillX(clientX));
  };

  dragEnd = () => {
    if (this.hovered < 0) return;
    const index = this.hovered;
    const item = getNavigationItems(this.props.__)[index];
    const finish = () => {
      this.moveTo(this.itemNodes()[index].offsetLeft);
      this.squash(() => {
        this.reset(index);
        this.leaveTimer = setTimeout(() => {
          if (item) forwardTo(item.url, item.params);
          this.setState({ selected: -1 });
        }, 140);
      });
    };
    if (this.slide) {
      this.slide.onfinish = () => {
        this.slideEnd(this.itemNodes()[index].offsetLeft);
        finish();
      };
    } else {
      finish();
    }
  };

  onPointerDown = (e) => {
    if (!e.isPrimary || e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    this.dragStart(e.clientX);
  };
  onPointerMove = (e) => {
    if (e.isPrimary) this.dragMove(e.clientX);
  };
  onPointerUp = (e) => {
    if (e.isPrimary) this.dragEnd();
  };
  onPointerCancel = () => this.reset();

  render() {
    const { __ } = this.props;
    const { dragging, hovered, selected } = this.state;
    return (
      <div
        ref={this.wrapperRef}
        className={`bottom-navigation-wrapper navigation-ios ${dragging ? 'dragging' : ''}`}
        onPointerDown={this.onPointerDown}
        onPointerMove={this.onPointerMove}
        onPointerUp={this.onPointerUp}
        onPointerCancel={this.onPointerCancel}
      >
        <span ref={this.pillRef} className="navigation-drag-pill">
          <span />
        </span>
        {getNavigationItems(__).map((item, index) => {
          const active = selected >= 0 ? index === selected : location.pathname === item.url;
          return (
            <div key={item.url} className={`${active ? 'navigation-active' : ''} ${dragging && index === hovered ? 'navigation-hover' : ''}`}>
              {active && !dragging && item.iconActive ? item.iconActive : item.icon}
              <SmallText>{item.text}</SmallText>
            </div>
          );
        })}
      </div>
    );
  }
}

export default withTranslation(BottomNavigationIOS);
